import { TagSchema } from "@redotech/redo-common-model-tags-common/tag";
import { Hazmat } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import { GroupSelectedRateSchema } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { PrintStatusSchema } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { OMSUserSchema } from "@redotech/redo-fulfillment-group-common/oms-user";
import {
  ItemParcelAssignmentsSchema,
  PackingParcelsSchema,
} from "@redotech/redo-fulfillment-group-common/packing-parcels";
import { FulfillmentGroupReassignmentReasonSchema } from "@redotech/redo-fulfillment-group-common/reassignment-reason";
import { WeightSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundParcelSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  CanadaPostOption,
  ShipmentPaymentOptionSchema,
  SpecialRateEligibility,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { DhlEcommerceLabelOptionsSchema } from "@redotech/redo-fulfillment-rate-common/outbound-shipment";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const FulfillmentGroupChangesSchema_ = z.discriminatedUnion("field", [
  z.object({
    field: z.literal("selectedShippingRate"),
    prev: GroupSelectedRateSchema.nullable(),
    next: GroupSelectedRateSchema.nullable(),
  }),
  z.object({
    field: z.literal("signatureRequired"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("adultSignatureRequired"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("specialRateEligibility"),
    prev: createEnum(SpecialRateEligibility).nullable(),
    next: createEnum(SpecialRateEligibility).nullable(),
  }),
  z.object({
    field: z.literal("selectedParcels"),
    prev: z.array(OutboundParcelSchema).nullable(),
    next: z.array(OutboundParcelSchema).nullable(),
  }),
  z.object({
    field: z.literal("hazmat"),
    prev: createEnum(Hazmat).nullable(),
    next: createEnum(Hazmat).nullable(),
  }),
  z.object({
    field: z.literal("dryIceWeight"),
    prev: WeightSchema.nullable(),
    next: WeightSchema.nullable(),
  }),
  z.object({
    field: z.literal("saturdayDelivery"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("buyReturnLabel"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("sendLabelPurchasedNotification"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("notifyMarketplace"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("fulfillmentDate"),
    prev: z.date().nullable(),
    next: z.date().nullable(),
  }),
  z.object({
    field: z.literal("ddp"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("overrideDeliveryMethod"),
    prev: createEnum(RedoShopifyDeliveryMethodType).nullable(),
    next: createEnum(RedoShopifyDeliveryMethodType).nullable(),
  }),
  z.object({
    field: z.literal("assignedUser"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("tags"),
    prev: z.array(TagSchema).nullable(),
    next: z.array(TagSchema).nullable(),
  }),
  z.object({
    field: z.literal("noteToBuyer"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("internalNote"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("printStatus"),
    prev: PrintStatusSchema.nullable(),
    next: PrintStatusSchema.nullable(),
  }),
  z.object({
    field: z.literal("scanVerified"),
    prev: z
      .object({
        verifiedBy: OMSUserSchema.nullish(),
        verifiedAt: z.date().nullish(),
      })
      .nullable(),
    next: z
      .object({
        verifiedBy: OMSUserSchema.nullish(),
        verifiedAt: z.date().nullish(),
      })
      .nullable(),
  }),
  z.object({
    field: z.literal("packingSlipTemplateId"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("personalizedSlipTemplateId"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
  }),
  z.object({
    field: z.literal("packingParcels"),
    prev: PackingParcelsSchema.nullable(),
    next: PackingParcelsSchema.nullable(),
  }),
  z.object({
    field: z.literal("itemParcelAssignments"),
    prev: ItemParcelAssignmentsSchema.nullable(),
    next: ItemParcelAssignmentsSchema.nullable(),
  }),
  z.object({
    field: z.literal("parcelsFinalized"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("shipmentPaymentOption"),
    prev: ShipmentPaymentOptionSchema.nullable(),
    next: ShipmentPaymentOptionSchema.nullable(),
  }),
  z.object({
    field: z.literal("carbonNeutral"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("canadaPostOption"),
    prev: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    }).nullable(),
    next: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    }).nullable(),
  }),
  z.object({
    field: z.literal("dhlEcommerceLabelOptions"),
    prev: DhlEcommerceLabelOptionsSchema.nullable(),
    next: DhlEcommerceLabelOptionsSchema.nullable(),
  }),
  z.object({
    field: z.literal("directDeliveryOnly"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("overrideResidentialAddress"),
    prev: z.boolean().nullable(),
    next: z.boolean().nullable(),
  }),
  z.object({
    field: z.literal("originLocation"),
    prev: z.string().nullable(),
    next: z.string(),
  }),
  z.object({
    field: z.literal("assignedFulfillmentService"),
    prev: z.string().nullable(),
    next: z.string().nullable(),
    reason: FulfillmentGroupReassignmentReasonSchema.nullish(),
    note: z.string().nullish(),
  }),
]);
export const FulfillmentGroupChangesSchema: z.ZodType<FulfillmentGroupChanges> =
  FulfillmentGroupChangesSchema_;
export type FulfillmentGroupChanges = z.infer<
  typeof FulfillmentGroupChangesSchema_
>;

export const FulfillmentGroupUpdateSource = { SCALE: "scale" } as const;
export type FulfillmentGroupUpdateSource =
  (typeof FulfillmentGroupUpdateSource)[keyof typeof FulfillmentGroupUpdateSource];

const fulfillmentGroupAuditSchema = z.object({
  id: z.bigint().optional(),
  accountId: z.bigint(),
  fulfillmentGroupId: z.string(),
  teamId: z.string(),
  userId: z.string(),
  source: createEnum(FulfillmentGroupUpdateSource).nullish(),
  changes: z.array(FulfillmentGroupChangesSchema_),
  createdAt: z.date(),
});
export const FulfillmentGroupAuditSchema: z.ZodType<FulfillmentGroupAudit> =
  fulfillmentGroupAuditSchema;
export type FulfillmentGroupAudit = z.infer<typeof fulfillmentGroupAuditSchema>;
